import { useRef, useEffect } from 'react'
import { useSelector, shallowEqual } from 'react-redux'
import injectSheet from 'react-jss'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import Header from '@/components/Header'
import Portal from '@/components/__commons/Portal'
import Footer from '@/components/Footer'
import PageTransition from '@/components/__commons/PageTransition'
import Scrollbar from '@/components/__new/Scrollbar'
import Nav from '@/components/__new/Nav'
import SVGSprite from '@/components/__layout/SVGSprite'
import ModalForm from '@/components/__modal/ModalForm'
import useResizeObserver from '@/hooks/useResizeObserver'
import useFontPreload from '@/hooks/useFontPreload'
import useLCPComplete from '@/hooks/useLCPComplete'
import { theme } from '@/styles/style'
import global from '@/styles/global'
import useScrollStore from '@/store/scroll'

function Layout({
  children,
  classes,
  preview,
}) {
  const $root = useRef()
  const lenis = useScrollStore((state) => state.lenis)
  const lcpComplete = useLCPComplete()

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { isLoading, strings } = useSelector((state) => ({
    isLoading: !state.loading.ready,
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Precache fonts
  ------------------------------*/
  useFontPreload({ fonts: ['Helvetica Neue', 'DIN Next LT Pro'] })

  useEffect(() => {
    if (!isLoading && lenis) lenis.start()
  }, [isLoading])

  // Effetto separato per dispatchiare l'evento react-app-ready solo quando LCP è completato
  useEffect(() => {
    // Dispatcha l'evento solo se il loading è terminato E l'LCP è completato
    if (!isLoading && lcpComplete) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(() => {
          const appReadyEvent = new CustomEvent('react-app-ready')
          document.dispatchEvent(appReadyEvent)
        })
      } else {
        const appReadyEvent = new CustomEvent('react-app-ready')
        document.dispatchEvent(appReadyEvent)
      }
    }
  }, [isLoading, lcpComplete])

  useResizeObserver({
    ref: $root.current,
    callback: () => ScrollTrigger.refresh(),
  })

  return (
    <div className={`${classes.root} content-root`}>
      <div
        className="wrapper"
        ref={$root}
      >
        {children}
        <Footer
          strings={strings}
          show={true}
          theme={theme}
          data={{
            title: strings.contact_society,
            text: strings.contact_address,
            imgRight: strings.footer_right,
          }}
          preview={preview}
        />
      </div>
      <SVGSprite />
      <PageTransition isLoading={isLoading} />
      <Portal className="header-root">
        <Header preview={preview} />
      </Portal>
      <Portal className="nav-root">
        <Nav />
      </Portal>
      <Portal className="scrollbar-root">
        <Scrollbar />
      </Portal>
      {strings.request_quote_form_id && strings.request_quote_form_id !== '#' ? (
        <Portal className={`modal-form-root-${strings.request_quote_form_id}`}>
          <ModalForm id={strings.request_quote_form_id} />
        </Portal>
      ) : null}
      {strings.request_3d_form_id && strings.request_3d_form_id !== '#' ? (
        <Portal className={`modal-form-root-${strings.request_3d_form_id}`}>
          <ModalForm id={strings.request_3d_form_id} />
        </Portal>
      ) : null}
      {strings.save_form_id && strings.save_form_id !== '#' ? (
        <Portal className={`modal-form-root-${strings.save_form_id}`}>
          <ModalForm id={strings.save_form_id} save={true} />
        </Portal>
      ) : null}
    </div>
  )
}

export default injectSheet(global)(Layout)
